/**
 * Authentication service for WCP Portal.
 * Provides token acquisition via MSAL.
 *
 * For React components, prefer useAuth() from @/contexts/AuthContext.
 * This module exposes a getAccessToken() helper for non-component code
 * (e.g., WebSocket connections in useExecStream).
 *
 * @module services/auth
 */

import { PublicClientApplication } from '@azure/msal-browser';
import { tokenRequest } from '@/config/msalConfig';
import { config } from '@/config';

let msalInstance: PublicClientApplication | null = null;

/** Register the MSAL instance (called once from main.tsx). */
export function initializeAuth(instance: PublicClientApplication): void {
  msalInstance = instance;
}

/** Acquire an access token silently for the Portal BFF API. Returns null on failure. */
export async function getAccessToken(): Promise<string | null> {
  return acquireToken(tokenRequest.scopes);
}

/** Acquire an access token silently for the Sandbox ManagementService. Returns null on failure. */
export async function getSandboxMgmtToken(): Promise<string | null> {
  if (!config.sandboxMgmtScope) return null;
  return acquireToken([config.sandboxMgmtScope]);
}

async function acquireToken(scopes: string[]): Promise<string | null> {
  if (!msalInstance) return null;

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes,
      account: accounts[0],
    });
    return response.accessToken;
  } catch {
    return null;
  }
}
